import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { Button, Dialog, Grid, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import { useSnackbar } from '../../../components/context/SnackbarContext';
import useAxios from '../../../hooks/useAxios';
import { useLoader } from "../../../components/context/LoaderContext";
import { Navigate } from "react-router-dom";
import DynamicFields from "components/common/CustomFields";
import moment from "moment-timezone";



function CreateNewContactDialog({ open, onClose, handleReloadFunc, dealId, salutationList, contactID, handleReloadParentFunc }) {

    const axiosInstance = useAxios();
    const { showSnackbar } = useSnackbar();
    const { showLoader, hideLoader } = useLoader();
    const [contactData, setContactData] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState();
    const [customFields, setCustomFields] = useState([]);

    useEffect(() => {
        if (contactID) {
            function fetchData() {
                showLoader();
                getContactDetail();
            }
            fetchData();
        } else {
            function fetchData() {
                showLoader();
                getData();
            }
            fetchData();
        }
    }, []);

    const getData = async () => {
        showLoader();
        await axiosInstance.get('api/contact/create').then((response) => {
            if (response.status === 200) {
                //console.log(response);
                setCustomFields(response?.data?.data?.customFields || []);
                hideLoader();
            }
        })
            .then((json) => { })
            .catch((error) => {
                if (error.response) {
                    hideLoader();
                    if (error.response.status === 400) {
                        showSnackbar(error.response.data.message, "warning");
                    }
                }
            });
    }

    //======Start:: Write a code for handle edit contact detail========//
    const getContactDetail = async () => {
        showLoader();
        await axiosInstance.get(`api/contact/edit/${contactID}`).then(response => {
            if (response.status === 200) {
                setContactData(response?.data?.data?.record);
                setSelectedAccount(response.data.data.selectedAccount)
                setCustomFields(response?.data?.data?.customFields || []);
                hideLoader();
            }
        }).then((json) => { })
            .catch((error) => {
                if (error.response) {
                    hideLoader();
                    if (error.response.status === 400) {
                        showSnackbar(error.response.data.message, "warning");
                        Navigate("/deal");
                    }
                }
            });
    }
    //======End:: Write a code for handle edit contact detail========//



    const buildInitialValues = (customFields) => {
        const initial = {
            salutation: contactData?.salutation || 'Mr',
            first_name: contactData.first_name || '',
            last_name: contactData.last_name || '',
            title: contactData.title || '',
            primary_email: contactData.primary_email || '',
            error_list: [],
            phone1: contactData?.phone1 || '',
        };

        const customValuesMap = {};

        if (Array.isArray(contactData?.custom_field_values)) {
            contactData.custom_field_values.forEach(item => {
                if (item?.field_name) {
                    customValuesMap[item.field_name.trim()] = item.value;
                }
            });
        }

        customFields.forEach(field => {
            const key = field.field_name?.trim();
            const fieldValue = customValuesMap[key];

            switch (field.display_type) {
                case 'checkbox':
                    try {
                        // If it's a stored JSON string like '["a", "b"]'
                        initial[key] = fieldValue ? JSON.parse(fieldValue) : [];
                    } catch {
                        initial[key] = Array.isArray(fieldValue) ? fieldValue : [];
                    }
                    break;

                case 'file':
                    // You can customize this if you support showing uploaded file names
                    initial[key] = []; // Typically empty unless you want to preview old uploads
                    break;

                case 'date':
                case 'datetime':
                    initial[key] = fieldValue ? moment(fieldValue) : '';
                    break;

                default:
                    initial[key] = fieldValue ?? '';
                    break;
            }
        });

        return initial;
    };

    const buildValidationSchema = (customFields) => {
        const schema = {
            first_name: yup.string().trim('First name cannot include space').strict(true).min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters').required('Enter first name'),
            last_name: yup.string().trim('Last name cannot include space').strict(true).min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters').required('Enter last name'),
            title: yup.string().min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),
            primary_email: yup.string().email('Please enter valid email address').min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),
            phone1: yup.string().max(25, 'Phone must be at most 25 characters'),
        };

        customFields.forEach(field => {
            const key = field?.field_name;
            const label = field?.label || field?.label;
            const isRequired = field?.is_required === 'yes';
            const minLength = field?.min_input ? parseInt(field.min_input) : undefined;
            const maxLength = field?.max_input ? parseInt(field.max_input) : undefined;

            switch (field?.display_type) {
                case 'text':
                case 'textarea':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    if (minLength) schema[key] = schema[key].min(minLength, `${label} must be at least ${minLength} characters`);
                    if (maxLength) schema[key] = schema[key].max(maxLength, `${label} must be at most ${maxLength} characters`);
                    break;

                case 'email':
                    schema[key] = yup.string().email('Enter a valid email');
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    if (minLength) schema[key] = schema[key].min(minLength, `${label} must be at least ${minLength} characters`);
                    if (maxLength) schema[key] = schema[key].max(maxLength, `${label} must be at most ${maxLength} characters`);
                    break;

                case 'phone':
                    schema[key] = yup
                        .string()
                        .matches(/^[0-9()+-\s]*$/, 'Enter a valid phone number');
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    if (minLength) schema[key] = schema[key].min(minLength, `${label} must be at least ${minLength} characters`);
                    if (maxLength) schema[key] = schema[key].max(maxLength, `${label} must be at most ${maxLength} characters`);
                    break;

                case 'number':
                    schema[key] = yup
                        .number()
                        .typeError(`${label} must be a number`);
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    if (minLength) schema[key] = schema[key].min(minLength, `${label} must be at least ${minLength} characters`);
                    if (maxLength) schema[key] = schema[key].max(maxLength, `${label} must be at most ${maxLength} characters`);
                    break;

                case 'select':
                    if (field?.is_multiple === 'yes') {
                        // MULTI-SELECT
                        schema[key] = yup.array().of(yup.string());
                        if (isRequired) {
                            schema[key] = schema[key].min(1, `Select at least one ${label}`);
                        }
                    } else {
                        // SINGLE SELECT
                        schema[key] = yup.string();
                        if (isRequired) {
                            schema[key] = schema[key].required(`${label} is required`);
                        }
                    }
                    break;
                case 'radio':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'checkbox':
                    schema[key] = yup.array();
                    if (isRequired) schema[key] = schema[key].min(1, `Select at least one ${label}`);
                    break;

                case 'date':
                case 'datetime':
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;

                case 'file':
                    schema[key] = yup.array().of(
                        yup
                            .mixed()
                            .test('fileSize', 'File too large', value => !value || value.size <= 4 * 1024 * 1024)
                    );
                    if (isRequired) {
                        schema[key] = schema[key].min(1, `Upload at least one ${label}`);
                    }
                    break;

                default:
                    schema[key] = yup.string();
                    if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                    break;
            }
        });

        return yup.object().shape(schema);
    };

    const staticFields = ['salutation', 'first_name', 'last_name', 'title', 'primary_email', 'phone1', 'deal_id'];



    //======Start:: Write a code for handle add and edit contact detail========//
    const handleSubmit = async (values, onSubmitProps) => {
        const formData = new FormData();

        const customFieldsData = {};

        Object.entries(values).forEach(([key, value]) => {
            if (staticFields.includes(key)) {
                // Static fields (e.g., first_name, etc.)
                if (Array.isArray(value)) {
                    value.forEach(file => formData.append(`${key}[]`, file));
                } else {
                    formData.append(key, value);
                }
            } else {
                // Custom fields
                const isFileArray = Array.isArray(value) && value.length && value[0] instanceof File;

                if (isFileArray) {
                    value.forEach((file, idx) => {
                        formData.append(`${key}[${idx}]`, file); // append each file individually
                    });
                } else {
                    customFieldsData[key] = value;
                }
            }
        });

        if (contactID) {
            formData.append('id', contactID);
            formData.append('account_id', selectedAccount || '');
        }

        if (dealId) {
            formData.append('deal_id', dealId);
        }

        // Append custom fields JSON string under one key
        formData.append('custom_fields', JSON.stringify(customFieldsData));

        showLoader()
        await axiosInstance.post(`/api/contact/${contactID ? 'update' : 'store'}`, formData).then(response => {
            if (response.status === 200) {
                hideLoader()
                showSnackbar(response.data.message, 'success')
                onClose()
                handleReloadFunc()
                handleReloadParentFunc()
            }
        }).then(json => {

        }).catch(error => {
            if (error.response) {
                hideLoader()
                onSubmitProps.setSubmitting(false)
                if (error.response.status === 422 || error.response.data.validation_errors) {
                    const errors = error.response.data.validation_errors;
                    Object.keys(errors).forEach(key => {
                        onSubmitProps.setFieldError(key, errors[key][0]);
                    });
                } else {
                    showSnackbar(error.response.data.message, 'error')
                }
            }
        });
    }
    //======End:: Write a code for handle add and edit contact detail========//


    return (
        <>
            <Dialog onClose={onClose} open={open}>
                <div className="dialog-title">
                    <h2>{contactID ? 'Update Contact' : 'Create New Contact'}</h2>
                    <Tooltip title="Close">
                        <button className="icon-button dialog-close" onClick={onClose}>
                            <Icon path={mdiClose} color={'currentColor'} size={1} />
                        </button>
                    </Tooltip>
                </div>
                <Formik
                    initialValues={buildInitialValues(customFields)}
                    validationSchema={buildValidationSchema(customFields)}
                    onSubmit={handleSubmit}
                    //enableReinitialize={contactID ? true : false}
                    enableReinitialize
                >
                    {props => {
                        const {
                            touched,
                            errors,
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                            setFieldTouched,
                        } = props;
                        return (
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={1.5}>
                                    <Grid item xs="auto">
                                        <TextField
                                            select
                                            className="no-label"
                                            value={values.salutation}
                                            name="salutation"
                                            variant="filled"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.salutation && touched.salutation}
                                            helperText={(errors.salutation && touched.salutation) && errors.salutation}

                                        >
                                            {Object.entries(salutationList).map(([key, value], index) => (
                                                <MenuItem key={index} value={key}>
                                                    {value}
                                                </MenuItem>
                                            ))}

                                        </TextField>
                                    </Grid>
                                    <Grid item xs>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="First Name"
                                                    variant="filled"
                                                    name="first_name"
                                                    value={values.first_name}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={errors.first_name && touched.first_name}
                                                    helperText={(errors.first_name && touched.first_name) && errors.first_name}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="Last Name"
                                                    variant="filled"
                                                    name="last_name"
                                                    value={values.last_name}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={errors.last_name && touched.last_name}
                                                    helperText={(errors.last_name && touched.last_name) && errors.last_name}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <TextField
                                    label="Title"
                                    variant="filled"
                                    name="title"
                                    value={values.title}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.title && touched.title}
                                    helperText={(errors.title && touched.title) && errors.title}
                                />

                                <Grid container spacing={1.5}>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Email"
                                            variant="filled"
                                            name="primary_email"
                                            value={values.primary_email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.primary_email && touched.primary_email}
                                            helperText={(errors.primary_email && touched.primary_email) && errors.primary_email}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Phone"
                                            variant="filled"
                                            name="phone1"
                                            value={values.phone1}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={errors.phone1 && touched.phone1}
                                            helperText={(errors.phone1 && touched.phone1) && errors.phone1}
                                        />
                                    </Grid>
                                </Grid>
                                {customFields?.length > 0 &&
                                    <Typography variant="title-medium-large-semibold" component={'h4'} className="mb-12">Custom Fields</Typography>
                                }

                                {customFields?.length > 0 &&
                                    <Grid container spacing={1.5}>
                                        {customFields.map((field, idx) => (
                                            <Grid item xs={['textarea', 'checkbox'].includes(field?.display_type) ? 12 : 6} key={idx}>
                                                <DynamicFields
                                                    key={idx}
                                                    field={field}
                                                    values={values}
                                                    handleChange={handleChange}
                                                    handleBlur={handleBlur}
                                                    errors={errors}
                                                    touched={touched}
                                                    setFieldValue={setFieldValue}
                                                    selectedFiles={contactData ? contactData?.custom_field_values : []}
                                                    setFieldTouched={setFieldTouched}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                }

                                <Stack direction="row" spacing={1.5} justifyContent={'flex-end'}>
                                    <Button type="submit">{contactID ? 'Update' : 'Create'}</Button>
                                    <Button color="secondary" onClick={onClose}>Cancel</Button>
                                </Stack>
                            </form>
                        );
                    }}
                </Formik>
            </Dialog>
        </>
    );
}

export default CreateNewContactDialog;